import { findColumn, findRow } from "../find";
import { applyRowColumnContentRebalance } from "../column-content-min";
import { cloneBlock } from "../factories";
import { sanitizeFillColumnImagesInRow } from "../image-height";
import {
  columnBlockIdsForDocument,
  isContiguousColumnBlockSelection,
  resolveOrderedColumnBlockIds,
} from "../selection/block-bulk-ops";
import type { Block } from "../types";
import type { EditorDraft } from "./types";
import { applyBlockMultiSelectionChange, applySelectionChange } from "./draft";

function clearBlockMultiSelection(draft: EditorDraft): void {
  draft.selectedBlockIds = [];
  draft.primaryBlockId = null;
  draft.anchorBlockId = null;
}

function selectionAfterColumnBlockDelete(
  draft: EditorDraft,
  rowId: string,
  columnId: string,
  deletedCount: number,
  lowestDeletedIndex: number,
): void {
  const column = findColumn(draft.document, rowId, columnId);
  if (!column) {
    clearBlockMultiSelection(draft);
    draft.selection = { kind: "email" };
    return;
  }

  if (column.blocks.length === 0) {
    clearBlockMultiSelection(draft);
    draft.selection = { kind: "column", rowId, columnId };
    return;
  }

  const nextBlock =
    column.blocks[lowestDeletedIndex] ??
    column.blocks[lowestDeletedIndex - 1] ??
    column.blocks[0];
  if (!nextBlock) {
    clearBlockMultiSelection(draft);
    draft.selection = { kind: "column", rowId, columnId };
    return;
  }

  if (deletedCount > 1) {
    clearBlockMultiSelection(draft);
    draft.selection = {
      kind: "block",
      rowId,
      columnId,
      blockId: nextBlock.id,
    };
    return;
  }

  draft.selection = {
    kind: "block",
    rowId,
    columnId,
    blockId: nextBlock.id,
  };
  draft.selectedBlockIds = [nextBlock.id];
  draft.primaryBlockId = nextBlock.id;
  draft.anchorBlockId = nextBlock.id;
}

export function copyBlocksForClipboard(
  document: EditorDraft["document"],
  blockIds: string[],
): Block[] {
  const ordered = resolveOrderedColumnBlockIds(document, blockIds);
  if (!ordered.ok) {
    return [];
  }

  const column = findColumn(document, ordered.rowId, ordered.columnId);
  if (!column) {
    return [];
  }

  const blocks: Block[] = [];
  const blockById = new Map(column.blocks.map(block => [block.id, block]));
  for (const blockId of ordered.blockIds) {
    const source = blockById.get(blockId);
    if (!source) {
      return [];
    }
    blocks.push(cloneBlock(source));
  }

  return blocks;
}

export function insertBlocksAfterAnchor(
  draft: EditorDraft,
  rowId: string,
  columnId: string,
  anchorBlockId: string,
  blocks: Block[],
): boolean {
  const column = findColumn(draft.document, rowId, columnId);
  if (!column || blocks.length === 0) {
    return false;
  }

  let insertIndex = column.blocks.findIndex(block => block.id === anchorBlockId);
  if (insertIndex < 0) {
    insertIndex = column.blocks.length - 1;
  }
  insertIndex += 1;

  const pastedIds: string[] = [];
  for (const block of blocks) {
    const copy = cloneBlock(block);
    column.blocks.splice(insertIndex, 0, copy);
    pastedIds.push(copy.id);
    insertIndex += 1;
  }

  const row = findRow(draft.document, rowId);
  if (row) {
    sanitizeFillColumnImagesInRow(row);
    applyRowColumnContentRebalance(draft.document, rowId);
  }

  applyBlockMultiSelectionChange(draft, {
    selectedBlockIds: pastedIds,
    primaryBlockId: pastedIds[pastedIds.length - 1] ?? null,
    anchorBlockId: pastedIds[0] ?? null,
  });
  return true;
}

export function deleteBlocksInColumn(
  draft: EditorDraft,
  rowId: string,
  columnId: string,
  blockIds: string[],
): boolean {
  const ordered = resolveOrderedColumnBlockIds(draft.document, blockIds);
  if (!ordered.ok) {
    return false;
  }

  const column = findColumn(draft.document, ordered.rowId, ordered.columnId);
  if (!column) {
    return false;
  }

  const blockIndexById = new Map(
    column.blocks.map((block, index) => [block.id, index] as const),
  );
  const indices: number[] = [];
  for (const blockId of ordered.blockIds) {
    const index = blockIndexById.get(blockId);
    if (index !== undefined) {
      indices.push(index);
    }
  }
  indices.sort((left, right) => right - left);

  if (indices.length !== ordered.blockIds.length) {
    return false;
  }

  const lowestDeletedIndex = Math.min(...indices);
  for (const index of indices) {
    column.blocks.splice(index, 1);
  }

  const row = findRow(draft.document, ordered.rowId);
  if (row) {
    sanitizeFillColumnImagesInRow(row);
  }

  selectionAfterColumnBlockDelete(
    draft,
    ordered.rowId,
    ordered.columnId,
    ordered.blockIds.length,
    lowestDeletedIndex,
  );
  return true;
}

export function duplicateBlocksInColumn(
  draft: EditorDraft,
  rowId: string,
  columnId: string,
  blockIds: string[],
): boolean {
  const ordered = resolveOrderedColumnBlockIds(draft.document, blockIds);
  if (!ordered.ok) {
    return false;
  }

  const copies = copyBlocksForClipboard(draft.document, ordered.blockIds);
  if (copies.length !== ordered.blockIds.length) {
    return false;
  }

  const anchorBlockId = ordered.blockIds[ordered.blockIds.length - 1] ?? "";
  return insertBlocksAfterAnchor(
    draft,
    ordered.rowId,
    ordered.columnId,
    anchorBlockId,
    copies,
  );
}

export function moveContiguousBlocksToSlot(
  draft: EditorDraft,
  blockIds: string[],
  to: { rowId: string; columnId: string; index: number },
): boolean {
  const ordered = resolveOrderedColumnBlockIds(draft.document, blockIds);
  if (!ordered.ok) {
    return false;
  }

  const columnBlockIds = columnBlockIdsForDocument(
    draft.document,
    ordered.rowId,
    ordered.columnId,
  );
  if (!isContiguousColumnBlockSelection(ordered.blockIds, columnBlockIds)) {
    return false;
  }

  const fromColumn = findColumn(draft.document, ordered.rowId, ordered.columnId);
  const toColumn = findColumn(draft.document, to.rowId, to.columnId);
  if (!fromColumn || !toColumn) {
    return false;
  }

  const fromIndices = ordered.blockIds.map(blockId =>
    fromColumn.blocks.findIndex(block => block.id === blockId),
  );
  if (fromIndices.some(index => index < 0)) {
    return false;
  }

  const blocksToMove = ordered.blockIds
    .map(blockId => fromColumn.blocks.find(block => block.id === blockId))
    .filter((block): block is Block => block !== undefined);
  if (blocksToMove.length !== ordered.blockIds.length) {
    return false;
  }

  for (let index = fromIndices.length - 1; index >= 0; index -= 1) {
    fromColumn.blocks.splice(fromIndices[index], 1);
  }

  let insertAt = to.index;
  const sameColumn =
    ordered.rowId === to.rowId && ordered.columnId === to.columnId;
  if (sameColumn) {
    insertAt -= fromIndices.filter(index => index < to.index).length;
  }

  toColumn.blocks.splice(insertAt, 0, ...blocksToMove);

  const fromRow = findRow(draft.document, ordered.rowId);
  if (fromRow) {
    sanitizeFillColumnImagesInRow(fromRow);
    applyRowColumnContentRebalance(draft.document, ordered.rowId);
  }
  if (!sameColumn) {
    const toRow = findRow(draft.document, to.rowId);
    if (toRow) {
      sanitizeFillColumnImagesInRow(toRow);
      applyRowColumnContentRebalance(draft.document, to.rowId);
    }
  }

  applyBlockMultiSelectionChange(draft, {
    selectedBlockIds: ordered.blockIds,
    primaryBlockId: ordered.blockIds[ordered.blockIds.length - 1] ?? null,
    anchorBlockId: ordered.blockIds[0] ?? null,
  });
  applySelectionChange(draft, {
    kind: "block",
    rowId: to.rowId,
    columnId: to.columnId,
    blockId: ordered.blockIds[ordered.blockIds.length - 1] ?? ordered.blockIds[0],
  });
  return true;
}

export function moveContiguousBlocksInColumnByDelta(
  draft: EditorDraft,
  rowId: string,
  columnId: string,
  blockIds: string[],
  delta: -1 | 1,
): boolean {
  const ordered = resolveOrderedColumnBlockIds(draft.document, blockIds);
  if (!ordered.ok) {
    return false;
  }

  const columnBlockIds = columnBlockIdsForDocument(
    draft.document,
    ordered.rowId,
    ordered.columnId,
  );
  if (!isContiguousColumnBlockSelection(ordered.blockIds, columnBlockIds)) {
    return false;
  }

  if (ordered.blockIds.length === 1) {
    const column = findColumn(draft.document, ordered.rowId, ordered.columnId);
    if (!column) {
      return false;
    }
    const index = column.blocks.findIndex(
      block => block.id === ordered.blockIds[0],
    );
    const targetIndex = index + delta;
    if (index < 0 || targetIndex < 0 || targetIndex >= column.blocks.length) {
      return false;
    }
    return moveContiguousBlocksToSlot(draft, blockIds, {
      rowId: ordered.rowId,
      columnId: ordered.columnId,
      index: targetIndex,
    });
  }

  const column = findColumn(draft.document, ordered.rowId, ordered.columnId);
  if (!column) {
    return false;
  }

  const indices = ordered.blockIds.map(blockId =>
    column.blocks.findIndex(block => block.id === blockId),
  );
  if (indices.some(index => index < 0)) {
    return false;
  }

  const edgeIndex = delta < 0 ? Math.min(...indices) : Math.max(...indices);
  const targetIndex = edgeIndex + delta;
  if (targetIndex < 0 || targetIndex >= column.blocks.length) {
    return false;
  }

  const blocksToMove = ordered.blockIds
    .map(blockId => column.blocks.find(block => block.id === blockId))
    .filter((block): block is Block => block !== undefined);
  if (blocksToMove.length !== ordered.blockIds.length) {
    return false;
  }

  for (let index = indices.length - 1; index >= 0; index -= 1) {
    column.blocks.splice(indices[index], 1);
  }

  let insertAt = targetIndex;
  if (delta > 0) {
    const removedBeforeTarget = indices.filter(index => index < edgeIndex + 1).length;
    insertAt = edgeIndex + 1 - removedBeforeTarget + 1;
  }

  column.blocks.splice(insertAt, 0, ...blocksToMove);

  const row = findRow(draft.document, ordered.rowId);
  if (row) {
    sanitizeFillColumnImagesInRow(row);
  }

  applyBlockMultiSelectionChange(draft, {
    selectedBlockIds: ordered.blockIds,
    primaryBlockId: ordered.blockIds[ordered.blockIds.length - 1] ?? null,
    anchorBlockId: ordered.blockIds[0] ?? null,
  });
  return true;
}

export function pasteBlocksFromClipboard(
  draft: EditorDraft,
  clipboardBlocks: Block[],
): boolean {
  if (clipboardBlocks.length === 0) {
    return false;
  }

  const lastSelectedBlockId =
    draft.selectedBlockIds[draft.selectedBlockIds.length - 1];
  const primaryBlockId = draft.primaryBlockId ?? lastSelectedBlockId ?? null;
  if (primaryBlockId === null) {
    return false;
  }

  const anchorBlockId: string = primaryBlockId;
  const ordered = resolveOrderedColumnBlockIds(draft.document, [anchorBlockId]);
  if (!ordered.ok) {
    return false;
  }

  return insertBlocksAfterAnchor(
    draft,
    ordered.rowId,
    ordered.columnId,
    anchorBlockId,
    clipboardBlocks,
  );
}

export function cutSelectedBlocks(
  draft: EditorDraft,
  blockIds: string[],
): Block[] {
  const copies = copyBlocksForClipboard(draft.document, blockIds);
  if (copies.length === 0) {
    return [];
  }

  const ordered = resolveOrderedColumnBlockIds(draft.document, blockIds);
  if (!ordered.ok) {
    return [];
  }

  if (!deleteBlocksInColumn(draft, ordered.rowId, ordered.columnId, ordered.blockIds)) {
    return [];
  }

  return copies;
}
